import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Info } from "lucide-react";

// Toast global em vez de alert(). Sucesso=verde, erro=vermelho 4.5s,
// aviso=amber, info=slate. Usa fallback `alert` quando o Provider não
// está montado (evita explodir antes do tree render).
const ToastContext = createContext(null);

// Um estilo por tipo — mesma linguagem visual dos cartões do app.
const ESTILO = {
  sucesso: { titulo: 'Tudo certo',  icone: CheckCircle2,  faixa: 'bg-emerald-500', chipBg: 'bg-emerald-50', chipTxt: 'text-emerald-600' },
  erro:    { titulo: 'Algo deu errado', icone: XCircle,   faixa: 'bg-red-500',     chipBg: 'bg-red-50',     chipTxt: 'text-red-600' },
  aviso:   { titulo: 'Atenção',     icone: AlertTriangle, faixa: 'bg-amber-500',   chipBg: 'bg-amber-50',   chipTxt: 'text-amber-600' },
  info:    { titulo: 'Aviso',       icone: Info,          faixa: 'bg-[#12192a]',   chipBg: 'bg-slate-100',  chipTxt: 'text-slate-700' },
};

// Aliases pt→en pra evitar TypeError em call sites que chamaram toast.error/success
// (mistura PT/EN ao longo do código). Fallback (sem Provider) também precisa
// expor os aliases pra não quebrar o app antes do tree montar.
export const useToast = () => useContext(ToastContext) || {
  show: (msg) => alert(msg),
  sucesso: (msg) => alert(msg),
  erro: (msg) => alert(msg),
  success: (msg) => alert(msg),
  error: (msg) => alert(msg),
  warning: (msg) => alert(msg),
  info: (msg) => alert(msg),
};

export const ToastProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const show = useCallback((msg, tipo = 'info', duracao = 3000) => {
    const id = Date.now() + Math.random();
    setItems((prev) => [...prev, { id, msg, tipo }]);
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), duracao);
  }, []);
  // MEMOIZADO: sem isso o `valor` era um objeto NOVO a cada render → todo consumer
  // que colocava `toast` em deps de useEffect/useCallback entrava em loop (o toast
  // de erro re-renderizava o provider → novo objeto → re-fetch → erro → spam).
  // Inclui todos os aliases (info/success/error/warning) pra nenhum call site cair
  // em método inexistente (era isso que fazia o "Devolver" virar no-op).
  const valor = useMemo(() => ({
    show: (msg) => show(msg, 'info'),
    sucesso: (msg) => show(msg, 'sucesso'),
    erro: (msg) => show(msg, 'erro', 4500),
    aviso: (msg) => show(msg, 'aviso'),
    info: (msg) => show(msg, 'info'),
    success: (msg) => show(msg, 'sucesso'),
    error: (msg) => show(msg, 'erro', 4500),
    warning: (msg) => show(msg, 'aviso'),
  }), [show]);
  return (
    <ToastContext.Provider value={valor}>
      {children}
      {/* Visual padrão de marketplace (dono, 23/09/2026): cartão branco com
          faixa e ícone da cor do tipo, em vez do balão escuro achatado.
          No celular sai EMBAIXO (área do polegar, não cobre o topo); no
          computador, canto superior direito. A forma de chamar (toast.sucesso,
          toast.erro…) NÃO mudou — as 29 telas seguem iguais. */}
      <div className="fixed z-[9999] flex flex-col gap-2 pointer-events-none
                      left-3 right-3 bottom-[max(5.5rem,calc(env(safe-area-inset-bottom)+5rem))]
                      sm:left-auto sm:bottom-auto sm:right-4 sm:top-[max(1rem,env(safe-area-inset-top))] sm:w-full sm:max-w-sm">
        {items.map((t) => {
          const e = ESTILO[t.tipo] || ESTILO.info;
          const Icone = e.icone;
          return (
            <div
              key={t.id}
              role="status"
              aria-live="polite"
              className="pointer-events-auto flex items-start gap-3 bg-white rounded-2xl
                         shadow-[0_8px_30px_rgba(2,6,23,0.18)] ring-1 ring-black/5
                         overflow-hidden animate-[toastIn_.22s_ease-out]"
            >
              <span className={`w-1.5 self-stretch flex-shrink-0 ${e.faixa}`} />
              <span className={`mt-3 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${e.chipBg}`}>
                <Icone size={16} className={e.chipTxt} />
              </span>
              <div className="py-3 pr-4 min-w-0">
                <p className="text-[13px] font-bold text-[#12192a] leading-snug">{e.titulo}</p>
                <p className="text-[13px] text-slate-600 leading-snug break-words">{t.msg}</p>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};
